import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function PricingAndMarkup() {
  return (
    <>
      <p>
        Every customer's invoice starts from what their Bedrock usage cost AWS. A rate plan adds our markup and any fees.
        This page explains the numbers you see on <ScreenLink href="/rate-plans">Rate plans</ScreenLink>,{" "}
        <ScreenLink href="/invoices">Invoices</ScreenLink> and{" "}
        <ScreenLink href="/revenue">Revenue &amp; margin</ScreenLink>.
      </p>

      <h2 id="tokens-to-invoice">From tokens to an invoice</h2>
      <ol>
        <li>
          The proxy counts each request's tokens by model and by type: input, output, cache read and cache write.
        </li>
        <li>
          Each day's tokens are priced at the model's AWS list price for that day, from{" "}
          <ScreenLink href="/model-prices">Model prices</ScreenLink>. That is the <strong>AWS cost</strong>.
        </li>
        <li>
          The customer's rate plan turns cost into price: the markup (or a fixed per-token rate), then any volume discount,
          trial credit, monthly minimum and platform fee. The exact order is in{" "}
          <HelpLink to="rate-plans" section="rates-and-tiers" />.
        </li>
        <li>Approved usage adjustments are applied. An invoice never goes below $0; any excess is carried to next month.</li>
        <li>
          At month end the month is closed. Its invoice is then fixed and cannot change. Anything found later is corrected
          on a later invoice.
        </li>
      </ol>
      <p>
        While a month is open, its invoice is a live preview: the same calculation, run on the usage so far. Amounts are
        rounded to the nearest millionth of a dollar per model, token type and day, so every report adds up to the same
        total.
      </p>

      <h2 id="markup-is-not-margin">Markup is not margin</h2>
      <p>
        Markup is measured against cost. Margin is measured against the invoice. A 30 % markup gives a 23.1 % margin, and
        both describe the same profit. The app shows them side by side so they are never confused.
      </p>
      <table>
        <tbody>
          <tr>
            <td>AWS cost</td>
            <td class="numeric">$1,000.00</td>
          </tr>
          <tr>
            <td>Invoice at 30 % markup</td>
            <td class="numeric">$1,300.00</td>
          </tr>
          <tr>
            <td>Profit</td>
            <td class="numeric">$300.00</td>
          </tr>
          <tr>
            <td>Markup, profit ÷ AWS cost</td>
            <td class="numeric">30 %</td>
          </tr>
          <tr>
            <td>Margin, profit ÷ invoice</td>
            <td class="numeric">23.1 %</td>
          </tr>
        </tbody>
      </table>
      <p>Common markups and the margin they give:</p>
      <table>
        <thead>
          <tr>
            <th class="numeric">Markup</th>
            <th class="numeric">Margin</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="numeric">10 %</td>
            <td class="numeric">9.1 %</td>
          </tr>
          <tr>
            <td class="numeric">20 %</td>
            <td class="numeric">16.7 %</td>
          </tr>
          <tr>
            <td class="numeric">30 %</td>
            <td class="numeric">23.1 %</td>
          </tr>
          <tr>
            <td class="numeric">50 %</td>
            <td class="numeric">33.3 %</td>
          </tr>
          <tr>
            <td class="numeric">100 %</td>
            <td class="numeric">50.0 %</td>
          </tr>
        </tbody>
      </table>
      <p>
        To reach a target margin, markup = margin ÷ (1 − margin). A 25 % margin needs a 33.3 % markup.
      </p>

      <h2 id="cost-basis">List price or actual AWS bill</h2>
      <p>
        A rate plan adds its markup to one of two costs. The same choice, called the <strong>cost basis</strong>, decides
        what profit is measured against on the finance screens.
      </p>
      <table>
        <thead>
          <tr>
            <th />
            <th>AWS list price</th>
            <th>Actual AWS bill</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Cost is</td>
            <td>Tokens × the published Bedrock price</td>
            <td>What AWS charged, from the customer's Cost and Usage Report, discounts and credits included</td>
          </tr>
          <tr>
            <td>Invoice ready</td>
            <td>When the month is closed</td>
            <td>When AWS marks the month's bill final, usually a few days later, and the month's AWS cost is finalized</td>
          </tr>
          <tr>
            <td>Use for</td>
            <td>Most customers (the default)</td>
            <td>Trusted customers only</td>
          </tr>
        </tbody>
      </table>
      <Callout>
        The Cost and Usage Report lives in the customer's own AWS account. A customer who can write there could change the
        report their own price is worked out from. Use the actual AWS bill only for customers you trust with that.
      </Callout>

      <Related to={["rate-plans", "closing-a-month", "aws-costs", "revenue-and-margin"]} />
    </>
  );
}
